/* eslint-disable babel/camelcase */
import {max, minBy, uniqBy, sortBy, isEqual} from 'lodash';

import {api} from '@utils/api';
import {
	excludeGSTPrice,
	calcualteProductGoodCause,
	convertCurrency,
} from '@utils/functions';

export const setInitialPrice = (
	isStore,
	product,
	retailer,
	isoCode,
	settings,
	origin,
	convertCurrencyFunc,
) => {
	if (!isStore) {
		const activeVariant = product.variances.filter(v => v.is_available);
		const obj = {};
		if (activeVariant.length === 1) {
			const rrpPrice = product.retailer_products.map(rp => {
				const s = rp.stocks.find(st => st.variance_id === activeVariant[0]._id);
				return {price: s.discounted_price || s.buy_price};
			});
			obj.rrp = minBy(rrpPrice, 'price').price;
		} else {
			obj.rrp = product.rrp;
		}
		obj.strikeThroughPrice = product.strikethrough;

		return obj;
	} else {
		return getDisplayStorePrice(
			product,
			retailer,
			isoCode,
			null,
			settings,
			isStore,
			origin,
			convertCurrencyFunc,
		);
	}
};

export const getDisplayStorePrice = (
	product,
	retailer,
	isoCode,
	selectedVariance = null,
	settings,
	isStore = false,
	origin,
	convertCurrencyFunc,
) => {
	if (!retailer) return;
	const retailerProduct = product.retailer_products.find(
		item => item.retailer_id === retailer._id,
	);
	if (!retailerProduct) {
		// eslint-disable-next-line no-console
		console.log('invalid product details found');
		return;
	}
	const obj = {
		rrp: 0,
		strikeThroughPrice: 0,
		goodCauseValue: 0,
	};
	if (retailerProduct.discounted_price) {
		if (retailerProduct.discounted_price > retailerProduct.buy_price) {
			obj.rrp = retailerProduct.discounted_price;
			obj.strikeThroughPrice = retailerProduct.buy_price;
			obj.goodCauseValue = calculateGoodCauseValue(
				settings,
				isoCode,
				product,
				excludeGSTPrice(
					convertCurrencyFunc(retailerProduct.discounted_price, false, false),
					origin,
				),
				isStore,
			);
		}
		if (
			retailerProduct.discounted_price &&
			retailerProduct.buy_price > retailerProduct.discounted_price
		) {
			obj.rrp = retailerProduct.discounted_price;
			obj.strikeThroughPrice = retailerProduct.buy_price;
			obj.goodCauseValue = calculateGoodCauseValue(
				settings,
				isoCode,
				product,
				excludeGSTPrice(
					convertCurrencyFunc(retailerProduct.discounted_price, false, false),
					origin,
				),
				isStore,
			);
		}
		if (
			retailerProduct.discounted_price &&
			retailerProduct.buy_price === retailerProduct.discounted_price
		) {
			obj.rrp = retailerProduct.discounted_price;
			obj.goodCauseValue = calculateGoodCauseValue(
				settings,
				isoCode,
				product,
				excludeGSTPrice(
					convertCurrencyFunc(retailerProduct.discounted_price, false, false),
					origin,
				),
				isStore,
			);
		}
	} else {
		if (retailerProduct.compare_at_price > retailerProduct.buy_price) {
			obj.rrp = retailerProduct.buy_price;
			obj.strikeThroughPrice = retailerProduct.compare_at_price;

			obj.goodCauseValue = calculateGoodCauseValue(
				settings,
				isoCode,
				product,
				excludeGSTPrice(
					convertCurrencyFunc(retailerProduct.buy_price, false, false),
					origin,
				),
				isStore,
			);
		} else {
			obj.rrp = retailerProduct.buy_price;
			obj.goodCauseValue = calculateGoodCauseValue(
				settings,
				isoCode,
				product,
				excludeGSTPrice(
					convertCurrencyFunc(retailerProduct.buy_price, false, false),
					origin,
				),
				isStore,
			);
		}
	}
	const bool = retailerProduct.stocks.every(
		(val, i, arr) => val && val.buy_price === arr[0].buy_price,
	);
	const activeVariants = retailerProduct.stocks.filter(s => s.stock);
	if (!bool && activeVariants.length > 1) {
		obj.hasMultiPricing = false;
	}
	if (selectedVariance) {
		obj.hasMultiPricing = true;
		const variance = retailerProduct.stocks.find(
			item => item.variance_id === selectedVariance,
		);

		if (variance) {
			if (variance.compare_at_price > variance.buy_price) {
				obj.rrp = variance.buy_price;
				obj.strikeThroughPrice = variance.compare_at_price;
				obj.goodCauseValue = calculateGoodCauseValue(
					settings,
					isoCode,
					product,
					excludeGSTPrice(
						convertCurrencyFunc(variance.buy_price, false, false),
						origin,
					),
					isStore,
				);
			} else if (
				variance.discounted_price &&
				variance.buy_price > variance.discounted_price
			) {
				obj.rrp = variance.discounted_price;
				obj.strikeThroughPrice = variance.buy_price;
				obj.goodCauseValue = calculateGoodCauseValue(
					settings,
					isoCode,
					product,
					excludeGSTPrice(
						convertCurrencyFunc(variance.discounted_price, false, false),
						origin,
					),
					isStore,
				);
			} else obj.rrp = variance.buy_price;

			if (!variance.compare_at_price && !variance.discounted_price)
				obj.strikeThroughPrice = 0;

			obj.goodCauseValue = calculateGoodCauseValue(
				settings,
				isoCode,
				product,
				excludeGSTPrice(convertCurrencyFunc(obj.rrp, false, false), origin),
				isStore,
			);
		}
	}

	return obj;
};

export const getAttributeOptionValue = data => {
	return data.newAttributeData.filter(function (item) {
		return !data.selectedOptionAttr.includes(item.name);
	});
};

export const calculateGoodCauseValue = (
	settings,
	isoCode,
	product,
	rrp,
	// eslint-disable-next-line no-unused-vars
	isStore = false,
	DonationValue = null,
) => {
	let goodCauseAmount = 0;
	const geoLocation = isoCode || 'au';

	let stripeFee = settings?.australia_stripe_fee;

	if (geoLocation.toLowerCase() !== 'au') {
		stripeFee = settings?.international_stripe_fee;
	}
	if (product?.retailer_products.length === 1) {
		const retailerProduct = product.retailer_products.find(
			item => item.retailer_id === product.retailers[0]._id,
		);

		let btFee = settings.default_bt_fee;
		if (retailerProduct && retailerProduct.transaction_charge_option) {
			btFee = Number(retailerProduct.transaction_charge_percentage);
		} else if (
			product.retailers &&
			product.retailers[0].transactionChargePercentage
		) {
			btFee = Number(product.retailers[0].transactionChargePercentage);
		}
		let retailerDonation = null;

		if (
			retailerProduct &&
			retailerProduct.retailerData &&
			retailerProduct.retailerData.charity
		) {
			retailerDonation = retailerProduct.retailerData;
		}
		goodCauseAmount = calcualteProductGoodCause(
			rrp,
			btFee,
			settings.good_cause_percentage,
			stripeFee,
			retailerDonation,
		);
	} else {
		if (!DonationValue) {
			DonationValue = product.retailers.filter(function (itm) {
				return itm.charity;
			})[0];
		}
		goodCauseAmount = calcualteProductGoodCause(
			rrp,
			settings.default_bt_fee,
			settings.good_cause_percentage,
			stripeFee,
			DonationValue,
		);
	}
	return goodCauseAmount;
};

export const createProductOptions = (
	variances,
	mas,
	retailerProducts,
	retailer,
	isStore,
	activeDropAuction,
	dispatch,
) => {
	let variants = [];
	if (isStore) {
		const rp = retailerProducts.find(r => r.retailer_id === retailer?._id);
		if (rp) {
			let stocks = [];
			stocks = rp.stocks
				.filter(s => s.stock > 0 && s.buy_price > 0)
				.map(m => m.variance_id);
			if (stocks.length) {
				variants = variances
					.filter(v => v.is_available === true && stocks.indexOf(v._id) > -1)
					.sort((a, b) =>
						Number(a.order) > Number(b.order)
							? 1
							: Number(b.order) > Number(a.order)
							? -1
							: 0,
					);
			}
		}
	} else {
		variants = variances
			.filter(
				v =>
					v.is_available === true &&
					retailerProducts.find(r =>
						r.stocks.find(s => s.stock > 0 && s.variance_id === v._id),
					),
			)
			.sort((a, b) =>
				Number(a.order) > Number(b.order)
					? 1
					: Number(b.order) > Number(a.order)
					? -1
					: 0,
			);
	}
	if (activeDropAuction) {
		variants.map(v => {
			const as = activeDropAuction.Auction_Stock.find(s => {
				return s.product_variance_id === v._id;
			});
			if (as) {
				v.auctionPrice = as.base_price;
				v.isAuction = true;
				return v;
			} else {
				v.isAuction = false;
				return v;
			}
		});
	}
	const attributes = variants.map(item => item.attributes);
	let attributeNames = [];
	for (let attribute of attributes) {
		for (let item of attribute) {
			if (!attributeNames.find(i => item.name === i.name)) {
				attributeNames.push(item);
			}
		}
	}

	const finalAttr = [];
	for (let attr of attributeNames) {
		const vals = [];
		for (let item of attributes) {
			// eslint-disable-next-line no-unused-vars
			for (const [index, [key, value]] of Object.entries(
				Object.entries(item),
			)) {
				if (attr.name === value.name) {
					vals.push(value);
				}
			}
		}

		let isMas = mas ? mas.find(i => i.attribute._id === attr._id) : null;
		if (isMas) {
			let values = uniqBy(vals, 'val_name').map(i => {
				let val = {val: i.val_name, available: true};
				let hasMasVal = mas.find(
					o =>
						o.attribute.values._id === i.val_id &&
						i._id === isMas.attribute._id,
				);

				if (hasMasVal) {
					val.src = hasMasVal.options.src || null;
					val.title = hasMasVal.options.title || null;
					// eslint-disable-next-line babel/camelcase
					val.mas_val = hasMasVal.options.value;
					val.position = hasMasVal.options.position;
				}
				val.position = val.position === 0 || val.position ? val.position : 1000;
				return val;
			});

			values = sortBy(values, 'position');

			finalAttr.push({
				name: attr.name,
				masName: isMas.label,
				type: isMas.type === 'visual' ? 'swatch' : null,
				guide: isMas.guide || null,
				values,
			});
			// dispatch({type: 'set_size_guide', sizeGuide: isMas.guide || ''});
		} else {
			finalAttr.push({
				name: attr.name,
				values: uniqBy(vals, 'val_name').map(i => ({
					val: i.val_name,
					available: true,
				})),
			});
		}
	}
	const value = sortBy(finalAttr, 'type');
	dispatch({type: 'set_attributes', value});
	getAvailableProductVariants(value, variances, retailerProducts, dispatch);
};

export const getAvailableProductVariants = (
	newAttributeData,
	variances,
	retailerProducts,
	dispatch,
) => {
	if (newAttributeData.length) {
		const activeVariants = variances.filter(v => v.is_available);
		let status = true;
		for (let index = 0; index < retailerProducts.length; index++) {
			if (status) {
				let stockArray = retailerProducts[index];
				const key = stockArray.stocks.find(s => s.discounted_price > 0)
					? 'discounted_price'
					: 'buy_price';
				const stocks = stockArray.stocks.filter(
					item => item.stock > 0 && item[key] > 0,
				);
				if (stocks && stocks.length > 0 && stocks[0][key]) {
					const stockFirstValue = stocks[0][key];
					for (let j = 0; j < stocks.length; j++) {
						if (stocks[j][key] !== stockFirstValue) {
							status = false;
							break;
						}
					}
				}
			} else {
				break;
			}
		}

		dispatch({
			type: 'setData',
			data: {hasMultiPricing: activeVariants.length > 1 ? status : true},
		});
		return true;
	} else {
		dispatch({type: 'setData', data: {hasMultiPricing: true}});
		return true;
	}
};

export const getDisplayPrice = (
	isStore,
	retailerProducts,
	selectedVariance,
	retailers,
	origin,
	currencyData,
) => {
	const obj = {
		rrp: 0,
		strikeThroughPrice: 0,
	};

	if (!isStore) {
		if (selectedVariance) {
			const retailerProduct = retailerProducts.find(
				item => item.retailer_id === retailers[0]._id,
			);

			const variance = retailerProduct.stocks.find(
				item => item.variance_id + 'l' === selectedVariance + 'l',
			);
			if (retailerProducts.length === 1 && variance) {
				if (
					convertCurrency(variance.compare_at_price, currencyData) &&
					convertCurrency(variance.compare_at_price, currencyData) >
						convertCurrency(variance.buy_price, currencyData)
				) {
					obj.rrp = excludeGSTPrice(
						convertCurrency(variance.buy_price, currencyData),
						origin,
					);
					obj.strikeThroughPrice = excludeGSTPrice(
						convertCurrency(variance.compare_at_price, currencyData),
						origin,
					);
				} else {
					obj.rrp = excludeGSTPrice(
						convertCurrency(variance.buy_price, currencyData),
						origin,
					);
					obj.strikeThroughPrice = excludeGSTPrice(
						convertCurrency(variance.buy_price, currencyData),
						origin,
					);
				}
			}
		}
	}
	return obj;
};

const changeString = str => {
	return str.replace(/'/g, "\\'");
};

export const getAttr = (attributes, name, selectedOptions) => {
	let status = false;
	let cond = [];
	for (let i = 0; i < attributes.length; i++) {
		const attribute = attributes[i];
		if (selectedOptions[attribute.name]) {
			if (Object.keys(selectedOptions).length > 1) {
				if (attribute.name !== name) {
					cond.push(
						`'${changeString(attribute.name)}' == '${changeString(
							attribute.name,
						)}' && '${changeString(attribute.val_name)}' == '${changeString(
							selectedOptions[attribute.name],
						)}'`,
					);
				}
			} else {
				cond.push(
					`'${changeString(attribute.name)}' == '${changeString(
						attribute.name,
					)}' && '${changeString(attribute.val_name)}' == '${changeString(
						selectedOptions[attribute.name],
					)}'`,
				);
			}
		}
	}
	try {
		if (eval(cond.join(' && '))) {
			status = true;
		}
	} catch (e) {
		// eslint-disable-next-line no-console
		console.log('eval condtion breaks');
	}
	return cond.length ? status : true;
};

export const toggleModalVisibility = (value, name, action) => {
	action({type: 'set_modal_visibiilty', name, value});
};

// export const handleMouseover = (ref, name, value) => {
// 	if (ref.current[slugify(name)]) {
// 		ref.current[slugify(name)].innerText = `${name}: ${value}`;
// 	}
// 	//const elements = document.getElementsByClassName(slugify(name));
// 	//if (elements && elements.length) {
// 	//console.log('handleMouseover handleMouseover handleMouseover');
// 	//elements[0].innerHTML = `${name}: ${value}`;
// 	//}
// };

// export const handleMouseout = (ref, name) => {
// 	if (ref.current[slugify(name)]) {
// 		let selectedTitle = document.getElementById(`input_${slugify(name)}`);
// 		selectedTitle = selectedTitle === undefined ? '' : selectedTitle?.value;

// 		if (selectedTitle)
// 			ref.current[slugify(name)].innerText = `${name}: ${selectedTitle}`;
// 		else ref.current[slugify(name)].innerText = `${name}:`;
// 	}
// };

export const handleMouseover = (name, value) => {
	const elements = document.getElementsByClassName(slugify(name));
	elements[0].innerHTML = `${name}: ${value}`;
};

export const handleMouseout = name => {
	const elements = document.getElementsByClassName(slugify(name));
	elements[0].innerHTML = `${name}`;
	let selectedTitle = document.getElementById(`input_${slugify(name)}`);
	selectedTitle = selectedTitle === undefined ? '' : selectedTitle?.value;
	if (selectedTitle) elements[0].innerHTML = `${name}: ${selectedTitle}`;
	else elements[0].innerHTML = `${name}:`;
};

export const slugify = str => {
	return str.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '-');
};

export const selectVariance = (
	attributeName,
	attributeValue,
	variances,
	isStore,
	retailerProducts,
	retailers,
	auctions,
	product,
	origin,
	state,
	dispatch,
	currencyData = null,
	settings,
	isoCode,
	convertCurrencyFunc,
) => {
	const obj = {
		stockError: {stock: '', bagError: '', error: false},
		quantity: 1,
	};
	const elem = document.getElementById(`selectedValue-${attributeName}`);
	if (elem) elem.innerHTML = attributeValue;

	const attributes = document.getElementsByClassName('attribute-label');
	if (attributes.length) attributes[0].classList.remove('cont--unavail');
	if (variances.length === 1 && variances[0].is_base_variance === true) {
		obj.selectedVariance = variances[0]._id;
		obj.varianceName = variances[0].attributes
			.map(v => `${v.name} ${v.val_name}`)
			.join(': ');
		obj.vName = variances[0].attributes.map(v => ({
			name: v.name,
			value: v.val_name,
		}));
		OnChangeVariant(obj, dispatch);
	} else {
		if (state.selectedOptions[attributeName] === attributeValue) {
			const arr = state.selectedOptions;
			delete arr[attributeName];
			obj.selectedOptions = arr;
		} else {
			obj.selectedOptions = {
				...state.selectedOptions,
				[attributeName]: attributeValue,
			};
		}
		if (!obj.selectedOptions[attributeName]) {
			const arr = obj.selectedOptions;
			delete arr[attributeName];
			obj.selectedOptions = arr;
		}

		obj.selectedOptionsArr = [...Object.keys(obj.selectedOptions)];
		dispatch({type: 'onSelectVariance', data: {...obj}});
		getAvailableAttributes(
			product,
			origin,
			variances,
			retailerProducts,
			auctions,
			isStore,
			retailers,
			state,
			dispatch,
			obj.selectedOptions,
			currencyData,
			settings,
			isoCode,
			convertCurrencyFunc,
		);
	}
};

export const getAvailableAttributes = async (
	product,
	origin,
	variances,
	retailerProducts,
	auctions,
	isStore,
	retailers,
	state,
	dispatch,
	selectedOptions,
	currencyData,
	settings,
	isoCode,
	convertCurrencyFunc,
) => {
	let obj = {};
	let variants = [];
	if (retailerProducts.length > 1)
		variants = variances.filter(v =>
			retailerProducts.find(r => r.stocks.find(s => s.variance_id === v._id)),
		);
	else variants = variances.filter(i => i.is_available === true);
	var retailerProduct = {};
	if (isStore) {
		retailerProduct = retailerProducts.find(
			item => item.retailer_id + 'l' === retailers[0]._id + 'l',
		);
		variants = retailerProduct?.stocks.map(v =>
			variants.find(
				a =>
					v.variance_id === a._id &&
					v.buy_price > 0 &&
					(v.stock === undefined || v.stock > 0),
			),
		);
		variants = variants.filter(v => v !== undefined);
	}
	obj.newAttributeData = state.newAttributeData?.map(i => {
		i.values = i.values.map(v => {
			v.available = true;
			return v;
		});
		return i;
	});

	state.newAttributeData?.forEach(i => {
		i = i.name;
		let av = variants.filter(item => {
			return getAttr(item.attributes, i, selectedOptions);
		});

		const attributes = av.map(item => item.attributes);
		let attributeNames = [];
		for (let attribute of attributes) {
			for (let item of attribute) {
				if (attributeNames.indexOf(item.name) === -1) {
					attributeNames.push(item.name);
				}
			}
		}

		const vals = {};
		for (let item of attributes) {
			// eslint-disable-next-line no-unused-vars
			for (const [index, [key, value]] of Object.entries(
				Object.entries(item),
			)) {
				vals[value.val_name] = true;
			}
		}

		obj.newAttributeData = state.newAttributeData?.map(j => {
			if (selectedOptions && Object.keys(selectedOptions).length > 1) {
				if (j.name === i) {
					j.values = j.values.map(v => {
						v.available = v.available ? (vals[v.val] ? true : false) : false;
						return v;
					});
				}
			} else {
				if (!selectedOptions[j.name]) {
					j.values = j.values.map(v => {
						v.available = v.available ? (vals[v.val] ? true : false) : false;
						return v;
					});
				}
			}
			return j;
		});

		state.newAttributeData?.forEach(k => {
			if (k === i) {
				let hasValue = k.values.find(
					v => v.val === selectedOptions[i] && v.available,
				);
				if (!hasValue) {
					let arr = selectedOptions;
					delete arr[i];
					obj.selectedOptions = arr;
				}
			}
		});
	});

	if (Object.keys(selectedOptions).length === state.newAttributeData.length) {
		const selected = variants.find(v => {
			let va = {};
			for (let i = 0; i < v.attributes.length; i++) {
				va[v.attributes[i].name] = v.attributes[i].val_name;
			}
			const so = {};
			for (let item in selectedOptions) {
				so[item] = selectedOptions[item];
			}
			return isEqual(va, so);
		});

		if (selected) {
			let name = [];
			for (let item of selected.attributes) {
				name.push(`${item.name} ${item.val_name}`);
			}

			obj.isAvailableStock = true;
			obj.selectedVariance = selected._id;
			obj.varianceName = selected.attributes
				.map(v => `${v.name} ${v.val_name}`)
				.join(': ');
			obj.vName = selected.attributes.map(v => ({
				name: v.name,
				value: v.val_name,
			}));

			if (isStore && state.retailer) obj.isSeeOptionActive = true;
			else if (product.retailer_products.length === 1)
				obj.isSeeOptionActive = true;

			const res = getDisplayStorePrice(
				product,
				state.retailer,
				isoCode,
				obj.selectedVariance,
				settings,
				isStore,
				origin,
				convertCurrencyFunc,
			);
			obj = {...obj, ...res};
			if (auctions && auctions.length > 0) {
				const auctionStock = auctions[0].Auction_Stock;
				const isAuctionStockAvailable = auctionStock.find(
					item => item.product_variance_id === state.selectedVariance,
				);

				if (
					isAuctionStockAvailable &&
					state.activeDropAuction &&
					isAuctionStockAvailable.stock > 0
				)
					obj.dropAuctionStockAvailable = true;
			}
		}
		dispatch({type: 'onSelectVariance', data: {...obj}});
	} else {
		getDisplayPrice(
			isStore,
			retailerProducts,
			state.selectedVariance,
			retailers,
			origin,
			currencyData,
			isoCode,
		);
	}
};

export const OnChangeVariant = (obj, dispatch) => {
	dispatch({type: 'onSelectVariance', data: {...obj}});
};

export const increaseQuantity = async (
	key,
	selectedStoreShop,
	retailerProducts,
	quantity,
	selectedVariance,
	productId,
	cart,
	dispatch,
	retailer,
) => {
	if (selectedStoreShop) {
		if (key === 'bag') {
			return checkStockByRetailerProduct(
				selectedStoreShop.retailer_products,
				key,
				quantity,
				selectedVariance,
				selectedStoreShop,
				productId,
				cart,
				dispatch,
				retailer,
			);
		} else {
			checkStockByRetailerProduct(
				selectedStoreShop.retailer_products,
				key,
				quantity,
				selectedVariance,
				selectedStoreShop,
				productId,
				cart,
				dispatch,
				retailer,
			);
		}
	} else {
		if (key === 'bag') {
			return checkStockByRetailerProduct(
				retailerProducts[0],
				key,
				quantity,
				selectedVariance,
				selectedStoreShop,
				productId,
				cart,
				dispatch,
				retailer,
			);
		} else {
			checkStockByRetailerProduct(
				retailerProducts[0],
				key,
				quantity,
				selectedVariance,
				selectedStoreShop,
				productId,
				cart,
				dispatch,
				retailer,
			);
		}
	}
};

export const decreaseQuantity = (quantity, dispatch) => {
	if (quantity > 1) {
		dispatch({type: 'set_quantity', value: quantity - 1});
	}
};

const checkStockByRetailerProduct = async (
	retailerProducts,
	key,
	quantity,
	selectedVariance,
	selectedStoreShop,
	productId,
	cart,
	dispatch,
	retailer,
) => {
	const state = {};
	let alreadyAddQuantity = 0;
	if (key === 'bag') {
		alreadyAddQuantity = await getProductAddedQuantity(
			cart,
			selectedVariance,
			productId,
			selectedStoreShop,
			retailer,
		);
	}

	const selectedVariant = retailerProducts.stocks.find(item => {
		return selectedVariance === item.variance_id;
	});
	if (
		quantity + alreadyAddQuantity < selectedVariant.stock ||
		(key === 'bag' && quantity + alreadyAddQuantity <= selectedVariant.stock)
	) {
		if (key === 'bag') {
			return {
				status: true,
				quantity: quantity,
				stockCart: alreadyAddQuantity,
			};
		} else {
			state.quantity = quantity + 1;
		}
	} else {
		state.stockError = {error: true};
		if (key === 'bag') {
			return {
				status: false,
				quantity: selectedVariant.stock - alreadyAddQuantity,
				stockCart: alreadyAddQuantity,
				stock: selectedVariant.stock,
			};
		} else {
			state.stockError = {
				bagError: '',
				stock:
					selectedVariant.stock === 1
						? '1 product'
						: selectedVariant.stock + ' products',
				error: true,
			};
		}
	}

	dispatch({type: 'setData', data: {...state}});
};

const getProductAddedQuantity = (
	cart,
	selectedVariance,
	productId,
	selectedStoreShop,
	retailer,
) => {
	const retailerId = selectedStoreShop
		? selectedStoreShop.store_data._id
		: retailer._id;
	return (
		cart.find(
			i =>
				i.variance_id === selectedVariance &&
				i.product_id === productId &&
				i.retailer_id === retailerId,
		)?.quantity || 0
	);
};

export const addToBag = async (
	product,
	state,
	dispatch,
	updateCartTotal,
	session,
	isModal,
	currencyData,
) => {
	const obj = {};
	const stockLimit = await increaseQuantity(
		'bag',
		state.selectedStoreShop,
		product.retailer_products,
		state.quantity,
		state.selectedVariance,
		product._id,
		state.cart,
		dispatch,
		state.retailer,
	);
	if (stockLimit.quantity === 0) {
		obj.stockError = {
			error: true,
			bagError: `Sorry, the maximum quantity available for this product is ${stockLimit.stock}. We have updated your quantity in your cart to ${stockLimit.stockCart}.`,
		};
	} else {
		if (state.storePrices && state.storePrices.length) {
			const retailerP = state.storePrices.find(
				s =>
					s.retailer_products.retailer_id ===
					state.selectedStoreShop.store_data._id,
			);
			if (retailerP) obj.retailer = retailerP.store_data;
		} else {
			obj.retailer = product.retailers[0];
		}

		const price = getProductLowestPrice(state.selectedStoreShop);
		const data = {
			// eslint-disable-next-line babel/camelcase
			product_id: product._id,
			// eslint-disable-next-line babel/camelcase
			product_name: product.product_name,
			// eslint-disable-next-line babel/camelcase
			variance_id: state.selectedVariance,
			quantity: state.quantity,
			// eslint-disable-next-line babel/camelcase
			retailer_id: state.selectedStoreShop.store_data._id,
			price: parseFloat(price.toFixed(2)),
			priceExTax: parseFloat(excludeGSTPrice(price, origin).toFixed(2)),
			varianceName: state.varianceName,
			currency: currencyData
				? {
						currency: currencyData.currency,
						audCurrency: currencyData.audCurrency,
						converted_rate: currencyData.converted_rate,
						live_rate: currencyData.live_rate,
						increased_rate: currencyData.increased_rate,
						round_off: currencyData.round_off,
				  }
				: '',
		};
		try {
			const result = await api(
				{
					url: '/cart/add',
					method: 'post',
					data,
				},
				session,
			);
			if (result) {
				obj.errorResponse = null;
				updateCartTotal(calcCartQty(result.cart.products));
				obj.cart = result.cart.products;
				const p = result.cart.products.find(i => {
					return (
						i.product_id === data.product_id &&
						i.retailer_id === data.retailer_id &&
						i.variance_id === data.variance_id
					);
				});
				if (p) {
					p.retailer = state.selectedStoreShop.store_data
						? state.selectedStoreShop.store_data.shop_name
						: state.retailer.shop_name;
					p.image = state.selectedImage;
					p.brand = product.manufacturer.name;
					p.vName = state.vName;
					p.quantity = state.quantity;
					p.price = parseFloat(p.price.toFixed(2));
					p.priceExTax = parseFloat(p?.priceExTax.toFixed(2) || 0);
					p.origin = origin;
					obj.addedCartProduct = p;
				}
			}
			if (!stockLimit.status) {
				obj.stockError = {
					...state.stockError,
					bagError: `Sorry, the maximum quantity available for this product is ${
						stockLimit.stock
					}. We have updated your quantity in your cart to ${
						stockLimit.quantity + stockLimit.stockCart
					}.`,
				};
			} else {
				if (!isModal) obj.openBagModal = true;
				else obj.addToCart = true;
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log(error, 'error');
			obj.errorResponse = 'Something went wrong. Please re-try.';
			obj.openBagModal = true;
		}
	}
	obj.loading = false;
	dispatch({type: 'setData', data: {...obj}});
};

export const addToBagStore = async (
	product,
	state,
	dispatch,
	updateCartTotal,
	session,
	origin,
	isModal,
	currencyData,
	isoCode,
) => {
	const response = await api(
		{
			url: '/product/get-store-product-prices',
			method: 'post',
			data: {
				// eslint-disable-next-line babel/camelcase
				product_id: product._id,
				// eslint-disable-next-line babel/camelcase
				product_variance_id: state.selectedVariance,
			},
		},
		session,
	);
	if (response.result) {
		let retailerResult = response.result;
		if (retailerResult.length > 1)
			retailerResult = retailerResult.filter(rt => {
				return rt.retailer_products.retailer_id === state.retailer._id;
			});
		const obj = setSelectedStore(
			product,
			retailerResult,
			state,
			origin,
			state.settings,
			product.isRPActive,
			product.auctions,
			isoCode,
		);
		const stockLimit = await increaseQuantity(
			'bag',
			state.selectedStoreShop,
			product.retailer_products,
			state.quantity,
			state.selectedVariance,
			product._id,
			state.cart,
			dispatch,
			state.retailer,
		);

		if (stockLimit.quantity === 0) {
			obj.stockError = {
				error: true,
				bagError: `Sorry, the maximum quantity available for this product is ${stockLimit.stock}. We have updated your quantity in your cart to ${stockLimit.stockCart}.`,
			};
		} else {
			const price = getProductLowestPrice(obj.selectedStoreShop);

			const data = {
				// eslint-disable-next-line babel/camelcase
				product_id: product._id,
				// eslint-disable-next-line babel/camelcase
				product_name: product.product_name,
				// eslint-disable-next-line babel/camelcase
				variance_id: state.selectedVariance,
				quantity: state.quantity,
				// eslint-disable-next-line babel/camelcase
				retailer_id: obj.selectedStoreShop.store_data._id,
				price: parseFloat(price.toFixed(2)),
				priceExTax: parseFloat(excludeGSTPrice(price, origin).toFixed(2)),
				varianceName: state.varianceName,
				currency: currencyData
					? {
							currency: currencyData.currency,
							audCurrency: currencyData.audCurrency,
							converted_rate: currencyData.converted_rate,
							live_rate: currencyData.live_rate,
							increased_rate: currencyData.increased_rate,
							round_off: currencyData.round_off,
					  }
					: '',
			};

			try {
				const result = await api(
					{
						url: '/cart/add',
						method: 'post',
						data,
					},
					session,
				);
				if (result) {
					obj.errorResponse = null;
					updateCartTotal(calcCartQty(result.cart.products));
					obj.cart = result.cart.products;
					const p = result.cart.products.find(i => {
						return (
							i.product_id === data.product_id &&
							i.retailer_id === data.retailer_id &&
							i.variance_id === data.variance_id
						);
					});
					if (p) {
						p.retailer = obj.selectedStoreShop.store_data.shop_name;
						p.image = state.selectedImage;
						p.brand = product.manufacturer.name;
						p.vName = state.vName;
						p.quantity = state.quantity;
						p.price = parseFloat(p.price.toFixed(2));
						p.priceExTax = p.priceExTax
							? parseFloat(p.priceExTax.toFixed(2))
							: 0;
						p.origin = origin;
						obj.addedCartProduct = p;
					}
				}
				if (!stockLimit.status) {
					obj.stockError = {
						...state.stockError,
						bagError: `Sorry, the maximum quantity available for this product is ${
							stockLimit.stock
						}. We have updated your quantity in your cart to ${
							stockLimit.quantity + stockLimit.stockCart
						}.`,
					};
				} else {
					if (!isModal) obj.openBagModal = true;
					else obj.addToCart = true;
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log(error, 'error');
				obj.errorResponse = 'Something went wrong. Please re-try.';
				obj.openBagModal = true;
			}
		}
		obj.loading = false;
		if (isModal) obj.selectedStoreShop = null;
		dispatch({type: 'setData', data: {...obj}});
	}
};

export const getAuction = (product, selectedVariance, isMulti = false) => {
	const variances = product.variances;
	if (variances) {
		const activeAuction = product.auctions[0];
		if (isMulti) {
			const auctionVariant = product.auctions[0].Auction_Stock.find(
				a => a.product_variance_id === selectedVariance,
			);
			if (auctionVariant) {
				// eslint-disable-next-line babel/camelcase
				activeAuction.base_price = auctionVariant.base_price;
				return activeAuction;
			} else return null;
		} else {
			const auctionVariant = variances.find(v => v._id === selectedVariance);
			if (auctionVariant && auctionVariant.isAuction) {
				// eslint-disable-next-line babel/camelcase
				activeAuction.base_price = auctionVariant.auctionPrice;
				return activeAuction;
			} else {
				return null;
			}
		}
	}
};

const setSelectedStore = (
	product,
	result,
	state,
	origin,
	settings,
	isRPActive,
	auctions,
	isoCode,
) => {
	const obj = {};
	let stores = result.filter(item => {
		return item.stock && item.buy_price;
	});
	stores = stores
		.map(value => {
			const price = !value.discounted_price
				? value.buy_price
				: value.discounted_price;
			let auction = null;
			if (auctions.length && value.store_data._id === auctions[0].retailer_id) {
				let dt = getAuction(product, state.selectedVariance, true);
				auction = dt;
			}
			return {...value, price, auction, show: true};
		})
		.sort((a, b) => {
			return a.base_price && b.base_price
				? a.price - b.price && a.base_price - b.base_price
				: a.price - b.price;
		});

	obj.storePrices = stores;
	const goodCauseValue = calcVariantGoodCause(
		result,
		state,
		origin,
		settings,
		isoCode,
	);
	obj.goodCauseAmount = goodCauseValue;
	if (isRPActive === 1) {
		if (stores.length === 0) {
			obj.isAvailableStock = false;
		} else {
			obj.selectedStoreShop = stores[0];
		}
	} else {
		if (stores.length === 0) {
			obj.isAvailableStock = false;
		} else {
			obj.isAvailableStock = true;
		}
	}
	return obj;
};

const getProductLowestPrice = shop => {
	let price = 0;
	if (shop.discounted_price) {
		if (shop.discounted_price > shop.buy_price) price = shop.buy_price;
		else price = shop.discounted_price;
	}
	if (!shop.discounted_price) {
		if (shop.compare_at_price && shop.compare_at_price > shop.buy_price)
			price = shop.buy_price;
		else price = shop.buy_price;
	}
	return price;
};

export const calcCartQty = products => {
	let qty = 0;
	for (let i = 0; i < products.length; i++) {
		qty += products[i].quantity;
	}
	return qty;
};

export const fetchProductPrices = async (
	product,
	state,
	dispatch,
	session,
	obj,
	origin,
	isoCode,
) => {
	try {
		dispatch({type: 'setData', data: {loading: true}});
		const response = await api(
			{
				url: '/product/get-store-product-prices',
				method: 'post',
				data: {
					// eslint-disable-next-line babel/camelcase
					product_id: product._id,
					// eslint-disable-next-line babel/camelcase
					product_variance_id: state.selectedVariance,
					catIds: product?.categories || [],
				},
			},
			session,
		);
		if (response.result) {
			const result = setSelectedStore(
				product,
				response.result,
				state,
				origin,
				state.settings,
				product.isRPActive,
				product.auctions,
				isoCode,
			);
			dispatch({type: 'setData', data: {...obj, ...result, loading: false}});
		}
	} catch (error) {
		// eslint-disable-next-line no-console
		console.log(error);
	}
	return true;
};

const calcVariantGoodCause = (result, state, origin, settings, isoCode) => {
	const highestPrice = [];
	if (!state.isSeeOptionActive && origin === 'au') {
		for (let index = 0; index < result.length; index++) {
			let price = result[index];
			if (
				excludeGSTPrice(price.discounted_price, origin) &&
				excludeGSTPrice(price.discounted_price, origin) > 0
			) {
				highestPrice.push(excludeGSTPrice(price.discounted_price, origin));
			} else {
				highestPrice.push(excludeGSTPrice(price.buy_price, origin));
			}
		}
	}
	if (highestPrice.length > 0) {
		let maxVal = max(highestPrice);
		let stripeFee = settings.australia_stripe_fee;
		if (isoCode && isoCode.toLowerCase() !== 'au') {
			stripeFee = settings.international_stripe_fee;
		}
		return calcualteProductGoodCause(
			maxVal,
			settings.default_bt_fee,
			settings.good_cause_percentage,
			stripeFee,
		);
	}
};

export const seeComparePrices = (
	product,
	session,
	state,
	dispatch,
	origin,
	isoCode,
	isEffect = false,
) => {
	const obj = {};
	if (!state.selectedVariance) {
		obj.isBtnClicked = !isEffect;
		const elem = getAttributeOptionValue({
			newAttributeData: state.newAttributeData,
			selectedOptionAttr: state.selectedOptionAttr,
		});
		obj.activeAttributes = elem;
		dispatch({type: 'setData', data: {...obj}});
		return;
	}
	obj.isSeeOptionActive = true;
	fetchProductPrices(product, state, dispatch, session, obj, origin, isoCode);
};
